import React, { useState } from "react";
import { FaCopy, FaExchangeAlt, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { Helmet } from "react-helmet";

export default function Base64Tool() {
  const [inputText, setInputText] = useState("");
  const [outputText, setOutputText] = useState("");
  const [error, setError] = useState("");
  const [mode, setMode] = useState("encode"); // "encode" or "decode"

  const handleConvert = () => {
    try {
      if (mode === "encode") {
        setOutputText(btoa(inputText));
      } else {
        setOutputText(atob(inputText));
      }
      setError("");
    } catch (err) {
      setError("Invalid input for decoding.");
      setOutputText("");
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(outputText);
  };

  return (
    <>
      <Helmet>
        <title>Base64 Encoder & Decoder - Free Online Tool | WebUtility.online</title>
        <meta
          name="description"
          content="Use our free Base64 Encoder & Decoder to easily convert text to Base64 and decode Base64 back to text. Fast, secure, and no login required."
        />
        <meta
          name="keywords"
          content="Base64 encoder, Base64 decoder, online Base64 tool, encode text to Base64, decode Base64 string, WebUtility"
        />
        {/* Open Graph Tags for Social Sharing */}
        <meta property="og:title" content="Base64 Encoder & Decoder - Free Online Tool" />
        <meta
          property="og:description"
          content="Encode and decode Base64 strings instantly with WebUtility's easy-to-use online Base64 tool."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://webutility.online/base64-tool" />
        <meta property="og:image" content="https://webutility.online/assets/og-base64.jpg" />
        {/* Twitter Card (Optional) */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Base64 Encoder & Decoder - WebUtility" />
        <meta
          name="twitter:description"
          content="Convert text to Base64 and decode Base64 easily using WebUtility's free online tool."
        />
        <meta name="twitter:image" content="https://webutility.online/assets/og-base64.jpg" />
      </Helmet>
      
      <div className="container mx-auto p-6 max-w-screen-2xl shadow-md rounded-md">
        {/* Heading */}
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-gray-800">Base64 Encoder & Decoder</h2>
          <p className="text-gray-600">Encode or decode Base64 strings easily.</p>
        </div>

        {/* Input Text */}
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">Enter Text:</label>
          <textarea
            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
            rows="4"
            placeholder="Enter text to encode or Base64 to decode..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
          ></textarea>
        </div>

        {/* Mode Toggle */}
        <div className="flex justify-between items-center mb-4">
          <button
            className={`px-5 py-2 rounded-lg font-semibold ${mode === "encode" ? "bg-blue-600 text-white" : "bg-gray-300 text-gray-700"}`}
            onClick={() => setMode("encode")}
          >
            Encode
          </button>
          <FaExchangeAlt className="text-gray-500 text-2xl" />
          <button
            className={`px-5 py-2 rounded-lg font-semibold ${mode === "decode" ? "bg-blue-600 text-white" : "bg-gray-300 text-gray-700"}`}
            onClick={() => setMode("decode")}
          >
            Decode
          </button>
        </div>

        {/* Convert Button */}
        <button
          onClick={handleConvert}
          className="flex items-center justify-center bg-green-600 text-white px-5 py-2 rounded-lg hover:bg-green-700 transition w-full"
        >
          <FaExchangeAlt className="mr-2" /> Convert
        </button>

        {/* Error Message */}
        {error && (
          <p className="text-red-500 mt-3 flex items-center">
            <FaTimesCircle className="mr-2" /> {error}
          </p>
        )}

        {/* Output */}
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-800">Result:</h3>
          <div className="bg-gray-100 p-4 rounded-lg flex justify-between items-center">
            <span className="break-all">{outputText || "No result yet"}</span>
            {outputText && (
              <button
                className="ml-4 text-blue-500 hover:text-blue-700"
                onClick={copyToClipboard}
              >
                <FaCopy />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Guide Section */}
      <section className="container mx-auto p-6 max-w-screen-2xl shadow-md rounded-md mt-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">🛠 How to Use the Base64 Encoder & Decoder</h2>
        <p className="text-gray-600 mb-4">
          The <strong>Base64 Encoder & Decoder</strong> tool allows you to easily convert text to Base64 encoding and decode Base64 strings back to text. This tool is fast, secure, and requires no registration.
        </p>
        <ul className="list-disc list-inside space-y-2 mb-4">
          <li>To encode, paste your plain text into the input box and click <strong>“Encode”</strong>.</li>
          <li>To decode, paste a Base64-encoded string into the input box and click <strong>“Decode”</strong>.</li>
          <li>Click <strong>“Convert”</strong> to see the result immediately.</li>
          <li>Click the <strong>copy icon</strong> to copy the result to your clipboard.</li>
        </ul>
        <p className="text-gray-600">
          This tool performs encoding and decoding on the client side, which means your data is kept private and is not sent to any external server.
        </p>
      </section>
    </>
  );
}
